// Generated by build-browser-modules.cjs from tested contract-source.

// Proposed API contract only. A browser ended event is not evidence of viewing.
// The future backend must validate its own session/time/progress records.
import {checkedCapability} from "./opnopoly-client.js";
const ROUTE = '/v1/monopoly/tutorial';
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ID = /^[-a-zA-Z0-9_]{16,80}$/;
const RECEIPT = /^[a-f0-9]{48}$/;
const fail = code => Object.assign(new Error(code), {code});
const object = value => value && typeof value === 'object' && !Array.isArray(value);
const copy = value => value == null ? null : JSON.parse(JSON.stringify(value));
const integer = value => Number.isSafeInteger(value) && value >= 0;
const exact = (value, fields) => object(value) && Object.keys(value).sort().join(',') === fields.slice().sort().join(',');
const accountScope = capability => capability && capability.userId + ':' + capability.tutorialVersion;

function checkedTutorialRequest(path, payload) {
    const fields = ['action','requestId','tutorialVersion'];
    if (payload && payload.action === 'complete') fields.push('sessionReceipt');
    if (path !== ROUTE || !exact(payload, fields) || !['start','complete'].includes(payload.action)
        || typeof payload.requestId !== 'string' || !ID.test(payload.requestId)
        || typeof payload.tutorialVersion !== 'string' || !/^[-a-zA-Z0-9_.]{1,80}$/.test(payload.tutorialVersion)
        || (payload.action === 'complete' && (typeof payload.sessionReceipt !== 'string' || !RECEIPT.test(payload.sessionReceipt)))) throw fail('INVALID_TUTORIAL_REQUEST');
    return 'POST';
}

function newId() {
    if (!globalThis.crypto || typeof globalThis.crypto.getRandomValues !== 'function') throw fail('SECURE_REQUEST_ID_UNAVAILABLE');
    return 'tutorial_' + Array.from(globalThis.crypto.getRandomValues(new Uint8Array(16)), b=>b.toString(16).padStart(2,'0')).join('');
}

class MonopolyTutorialClient {
    constructor(api, {clock = ()=>Date.now(), createRequestId = newId, onPending = ()=>{}} = {}) {
        if (!api || typeof api.call !== 'function' || !api.identity || !UUID.test(api.identity.userId || '')
            || !UUID.test(api.identity.gameId || '')) throw fail('AUTHENTICATED_ADAPTER_REQUIRED');
        this.api = api; this.identity = {userId:api.identity.userId, gameId:api.identity.gameId};
        this.clock = clock; this.createRequestId = createRequestId; this.onPending = onPending;
        this.capability = null; this.session = null; this.pending = null;
        this.inFlight = null; this.generation = 0; this.disposed = false;
    }
    setCapability(value) {
        let next = null;
        try { next = checkedCapability(value, this.identity, this.clock()); } catch (_) { /* unavailable */ }
        if (accountScope(next) !== accountScope(this.capability)) {this.session = null; this.pending = null;}
        this.capability = next; this.generation += 1; this.inFlight = null;
    }
    requireCapability() {
        if (this.disposed || this.api.disposed || this.api.identity.userId !== this.identity.userId
            || this.api.identity.gameId !== this.identity.gameId) throw fail('MONOPOLY_UNAVAILABLE');
        return checkedCapability(this.capability, this.identity, this.clock());
    }
    getPending() {return copy(this.pending);}
    restorePending(value) {
        const capability = this.requireCapability();
        if (this.pending || this.inFlight || !exact(value,['scope','path','payload'])
            || value.scope !== accountScope(capability)) throw fail('INVALID_TUTORIAL_PENDING');
        checkedTutorialRequest(value.path, value.payload);
        if (value.payload.tutorialVersion !== capability.tutorialVersion) throw fail('INVALID_TUTORIAL_PENDING');
        this.pending = copy(value);
    }
    start() {
        const capability = this.requireCapability();
        if (this.pending) {
            if (this.pending.payload.action !== 'start') throw fail('TUTORIAL_COMPLETION_PENDING');
            return this.retryPending();
        }
        return this.begin({action:'start', tutorialVersion:capability.tutorialVersion});
    }
    complete() {
        const capability = this.requireCapability();
        if (this.pending && this.pending.payload.action === 'complete') return this.retryPending();
        if (!this.session || this.session.tutorialVersion !== capability.tutorialVersion) throw fail('TUTORIAL_START_REQUIRED');
        if (this.inFlight) throw fail('TUTORIAL_REQUEST_PENDING');
        return this.begin({action:'complete', tutorialVersion:capability.tutorialVersion, sessionReceipt:this.session.sessionReceipt});
    }
    begin(body) {
        const capability = this.requireCapability();
        const payload = {...body, requestId:this.createRequestId()};
        checkedTutorialRequest(ROUTE, payload);
        this.pending = {scope:accountScope(capability),path:ROUTE,payload};
        return this.retryPending();
    }
    retryPending() {
        const capability = this.requireCapability();
        if (!this.pending) throw fail('NO_TUTORIAL_PENDING');
        if (this.inFlight) return this.inFlight;
        const pending = this.getPending(), generation = this.generation;
        checkedTutorialRequest(pending.path, pending.payload);
        if (pending.scope !== accountScope(capability) || pending.payload.tutorialVersion !== capability.tutorialVersion) throw fail('INVALID_TUTORIAL_PENDING');
        this.onPending(copy(pending));
        const operation = (async () => {
            const result = await this.api.call(pending.path,copy(pending.payload));
            this.requireCapability();
            if (this.generation !== generation) throw fail('STALE_RESPONSE');
            if (!object(result) || result.requestId !== pending.payload.requestId || result.userId !== capability.userId
                || result.tutorialVersion !== capability.tutorialVersion) throw fail('INVALID_TUTORIAL_RESULT');
            if (pending.payload.action === 'start') {
                if (result.status !== 'STARTED' || typeof result.sessionReceipt !== 'string' || !RECEIPT.test(result.sessionReceipt)
                    || !integer(result.issuedAt) || !integer(result.notBefore) || !integer(result.expiresAt)
                    || result.notBefore !== result.issuedAt + 26000 || result.expiresAt <= result.notBefore) throw fail('INVALID_TUTORIAL_RESULT');
                this.session = {tutorialVersion:result.tutorialVersion,sessionReceipt:result.sessionReceipt,
                    issuedAt:result.issuedAt,notBefore:result.notBefore,expiresAt:result.expiresAt};
                // Keep the successful start intent, so reopening can recover the
                // same server receipt by idempotently retrying this start request.
                return copy(this.session);
            }
            if (result.status !== 'COMPLETED' || result.completed !== true) throw fail('TUTORIAL_NOT_COMPLETED');
            this.pending = null; this.session = null;
            try { this.onPending(null); } catch (_) { /* same completion ID may be retried safely */ }
            // Do not mutate Monopoly state. Refresh its authenticated state and
            // require tutorial.completed there before choosing a character/rolling.
            return {tutorialVersion:result.tutorialVersion,completed:true,needsStateRefresh:true};
        })();
        this.inFlight = operation;
        const clear = () => {if(this.inFlight === operation)this.inFlight = null;};
        operation.then(clear,clear);
        return operation;
    }
    dispose() {
        this.disposed = true; this.generation += 1; this.capability = null;
        this.session = null; this.pending = null; this.inFlight = null;
    }
}

export {MonopolyTutorialClient, checkedTutorialRequest, ROUTE};
